import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SSEEvent, useSSE } from '@API/useSSE';
import { usePostUserMessage, useStartAgenticSession } from '@components/Copilot/agentHooks';
import { isDeepEqual } from '@fabric-ui/utils';
import useDebounce from '@hooks/useDebounce';
import {
  AgenticWorkflowMode,
  ChatbotErrorType,
  ChatMessage,
  CopilotChatMessageType,
  TAgentState,
  TCopilotError,
  TSessionProps,
} from './types';

export enum AgentEventType {
  THINKING = 'thinking',
  TOOL_CALLING = 'toolCalling',
  ASSISTANT_RESPONSE = 'assistantResponse',
  ASSISTANT_READY = 'assistantReady',
  PRE_HOOK = 'preHook',
  ERROR = 'error',
  WORKFLOW_DONE = 'workflowDone',
  CUSTOM_OPERATION_INITIATED = 'customOperationInitiated',
  CUSTOM_OPERATION_STARTED = 'customOperationStarted',
  CUSTOM_OPERATION_RESULT = 'customOperationResult',
}

export type AgentSSEEvent = {
  eventType: AgentEventType;
  timestamp: string;
  description: string;
  markdownContent?: string;
  state?: any;
  error?: {
    message: string;
    type?: string;
  };
  operationType?: string;
  success?: boolean;
  operationOutput?: {
    summary?: string;
  };
  sessionMetadata?: {
    limited_context?: boolean;
  };
  loopId?: string;
};

const CUSTOM_OPERATION_MESSAGE_CONFIG = {
  SUMMARIZE_CHAT: {
    messageType: CopilotChatMessageType.CONVERSATION_MESSAGE_TYPE_SUMMARY,
    getContent: data => data?.summary,
  },
};

const DEBOUNCED_THINKING_TEXT_MS = 500;
const INITIAL_DELAY_MS = 3_000;
const DEFAULT_THINKING_TEXT = 'Thinking';

type ThinkingEvent = {
  text: string;
  timestamp: string;
};

const useThinkingText = () => {
  const [events, setEvents] = useState<ThinkingEvent[]>([]);
  const [showEvents, setShowEvents] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);

  useEffect(() => {
    if (!timerStarted) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowEvents(true);
    }, INITIAL_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [timerStarted]);

  const start = useCallback(() => {
    setTimerStarted(true);
  }, []);

  const addEvent = useCallback((event: AgentSSEEvent) => {
    if ([AgentEventType.THINKING, AgentEventType.TOOL_CALLING, AgentEventType.CUSTOM_OPERATION_STARTED].includes(event.eventType)) {
      setEvents(prev => [...prev, { text: event.description ?? DEFAULT_THINKING_TEXT, timestamp: event.timestamp }]);
    }
  }, []);

  const reset = useCallback(() => {
    setEvents([]);
    setShowEvents(false);
    setTimerStarted(false);
  }, []);
  const currentText = `${showEvents && events.length > 0 ? events[events.length - 1].text : DEFAULT_THINKING_TEXT}...`;
  const { debouncedValue: debouncedText } = useDebounce(currentText, DEBOUNCED_THINKING_TEXT_MS);

  return {
    text: debouncedText,
    start,
    addEvent,
    reset,
    isShowingEvents: showEvents && events.length > 0,
  };
};

export const useAgenticConversationSSE = ({
  isCopilotRunning = false,
  sessionId,
  addMessage,
  setErrorMessage,
  setSessionId,
  lastSessionProps,
  excludeStateUpdate,
  stateRef,
}: {
  isCopilotRunning: boolean;
  sessionId: string;
  addMessage: (message: ChatMessage) => void;
  setErrorMessage: React.Dispatch<React.SetStateAction<TCopilotError | null>>;
  setSessionId: (sessionId: string) => void;
  lastSessionProps: TSessionProps;
  excludeStateUpdate: boolean;
  stateRef: React.RefObject<{ currentState: TAgentState; lastKnownState: TAgentState }>;
}) => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [sseEnabled, setSseEnabled] = useState(false);
  const [isReducedContextMode, setIsReducedContextMode] = useState(false);
  const onCustomOperationSuccessRef = useRef<((data: Record<string, any>) => void) | null>(null);
  const { text: thinkingText, start: startThinkingTimer, addEvent, reset: resetThinkingText, isShowingEvents } = useThinkingText();

  const { mutate: startSession, isPending: isStartingSession } = useStartAgenticSession({
    onSuccess: ({ sessionId }: any) => {
      setSessionId(sessionId);
      setSseEnabled(true);
      setIsProcessing(true);
    },
    onError: () => {
      setErrorMessage({
        message: 'Something went wrong while starting the session. Please try again.',
        type: ChatbotErrorType.INTERNAL,
      });
      setSseEnabled(false);
    },
  });

  const { mutate: postMessageMutation, isPending: isPosting } = usePostUserMessage({
    onError: () => {
      setErrorMessage({
        message: 'Something went wrong. Please try again.',
        type: ChatbotErrorType.INTERNAL,
      });
      setIsProcessing(false);
    },
  });

  useEffect(() => {
    if (isCopilotRunning && !sessionId && lastSessionProps?.agentId) {
      startSession({
        agenticSessionInput: {
          agentId: lastSessionProps.agentId,
          workflowMode: AgenticWorkflowMode.AGENTIC_WORKFLOW_MODE_INTERACTIVE,
          additionalParams: lastSessionProps.additionalParams,
        },
      });
    }
    if (isCopilotRunning && sessionId) {
      setSseEnabled(true);
    }
    if (!isCopilotRunning) {
      setSseEnabled(false);
    }
    setIsReducedContextMode(false);
  }, [isCopilotRunning, sessionId, lastSessionProps]);

  const handleSSEMessage = useCallback(
    (event: SSEEvent<AgentSSEEvent>) => {
      const response = event.data;

      const stopProcessing = () => {
        setIsProcessing(false);
        resetThinkingText();
      };

      const setError = (error?: { message?: string; type?: string }) => {
        const errorMessage = error?.message || 'Something went wrong. Please try again.';

        setErrorMessage({
          message: errorMessage,
          type: (error?.type as ChatbotErrorType) || ChatbotErrorType.INTERNAL,
        });

        return errorMessage;
      };

      const eventHandlers: Record<AgentEventType, (response: AgentSSEEvent) => void> = {
        [AgentEventType.PRE_HOOK]: () => {
          // no-op
        },

        [AgentEventType.THINKING]: response => {
          setIsProcessing(true);
          addEvent(response);
        },

        [AgentEventType.TOOL_CALLING]: response => {
          setIsProcessing(true);
          addEvent(response);
        },

        [AgentEventType.CUSTOM_OPERATION_INITIATED]: () => {
          setIsProcessing(true);
          startThinkingTimer();
        },

        [AgentEventType.CUSTOM_OPERATION_STARTED]: response => {
          setIsProcessing(true);
          startThinkingTimer();
          addEvent(response);
        },

        [AgentEventType.ASSISTANT_RESPONSE]: response => {
          const messageContent = response.markdownContent;

          if (messageContent) {
            const message = {
              content: messageContent,
              state: response.state && hasMeaningfulValue(response.state) ? response.state : undefined,
              timestamp: response.timestamp,
              type: CopilotChatMessageType.CONVERSATION_MESSAGE_TYPE_AI,
              loopId: response.loopId,
            };
            addMessage(message);
            setErrorMessage(null);

            lastSessionProps?.onAgentResponse?.(message);
          }

          stopProcessing();
        },

        [AgentEventType.ASSISTANT_READY]: response => {
          setIsReducedContextMode(!!response.sessionMetadata?.limited_context);
          stopProcessing();
        },

        [AgentEventType.WORKFLOW_DONE]: () => {
          stopProcessing();
          setSseEnabled(false);
        },

        [AgentEventType.CUSTOM_OPERATION_RESULT]: response => {
          if (response.success && response.operationType) {
            const config = CUSTOM_OPERATION_MESSAGE_CONFIG[response.operationType];

            addMessage({
              content: config.getContent(response.operationOutput!),
              timestamp: response.timestamp,
              type: config.messageType,
            });

            setErrorMessage(null);

            if (onCustomOperationSuccessRef.current) {
              onCustomOperationSuccessRef.current(response.operationOutput!);
              onCustomOperationSuccessRef.current = null;
            }
          } else {
            onCustomOperationSuccessRef.current = null;
            setError(response.error);
          }

          stopProcessing();
        },

        [AgentEventType.ERROR]: response => {
          const errorMessage = setError(response.error);

          addMessage({
            content: errorMessage,
            timestamp: response.timestamp,
            type: CopilotChatMessageType.ERROR,
            isError: true,
          });

          setSseEnabled(false);
          stopProcessing();
        },
      };
      eventHandlers[response.eventType]?.(response);
    },
    [addEvent, addMessage, setErrorMessage, resetThinkingText, startThinkingTimer, lastSessionProps?.onAgentResponse]
  );

  useSSE<AgentSSEEvent>({
    url: `agent-events/${sessionId}`,
    enabled: sseEnabled && isCopilotRunning && !!sessionId,
    onMessage: handleSSEMessage,
  });

  const triggerStart = useCallback(() => {
    if (lastSessionProps?.agentId) {
      startSession({
        agenticSessionInput: {
          agentId: lastSessionProps.agentId,
          workflowMode: AgenticWorkflowMode.AGENTIC_WORKFLOW_MODE_INTERACTIVE,
          additionalParams: lastSessionProps.additionalParams,
        },
      });
    }
  }, [startSession, lastSessionProps]);

  const postMessage = ({ sessionId: sid, message }: { sessionId: string; message: string }) => {
    setIsProcessing(true);
    startThinkingTimer();

    if (excludeStateUpdate) {
      postMessageMutation({ sessionId: sid, message });
    } else {
      const { currentState, lastKnownState } = stateRef.current;
      const isStateUpdated = lastKnownState === null || !isDeepEqual(currentState, lastKnownState);

      if (isStateUpdated) {
        Object.assign(stateRef.current, { lastKnownState: currentState });
      }
      postMessageMutation({ sessionId: sid, message, stateUpdate: currentState, isStateUpdated });
    }
  };

  return {
    postMessage,
    triggerStart,
    isPollingEnabled: isStartingSession || isPosting || isProcessing,
    thinkingText,
    isShowingEvents,
    onCustomOperationSuccessRef,
    isReducedContextMode,
  };
};

const hasMeaningfulValue = obj => Object.values(obj).some(v => v && !(Array.isArray(v) && v.length === 0));
